import Button from "components/common/Button";
import TextareaInput from "components/common/InputField/TextareaInput";
import TextInput from "components/common/InputField/TextInput";
import ModalContainer from "components/common/ModalContainer";
import { getTriggerName } from "helpers/getAgentName";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import { uniqBy } from "lodash";
import React, { useEffect, useState } from "react";
import { workforceActions } from "redux/Ldb/actions";
import { AccountPermissions } from "types/permissions";
import { useChannel } from "ably/react";
import pako from "pako";
import { getFromStorage, hasHTMLTag, sanitizeHtml } from "helpers";
import CustomTable from "components/common/CustomTable";
import moment from "moment";
import useGetSingleTask from "hooks/workforceHooks/useGetSingleTask";
import toast from "react-hot-toast";
import CustomToast from "components/common/CustomToast";

const Preview = ({
    openPreview,
    setOpenPreview,
    agentType,
    agentName,
    steps,
    trigger,
    agentIsActive,
    rawAgentType,
}: {
    openPreview: boolean;
    setOpenPreview: (open: boolean) => void;
    agentType: string;
    agentName: string;
    steps: Record<string, any>[];
    trigger: string;
    agentIsActive;
    rawAgentType: string;
}) => {
    const aiFlow = [
        {
            title: "Email order is received and detected",
            icon: "ri-user-line text-white bg-[#465569]",
        },
        {
            title: "AI team-mate extracts order information",
        },
        {
            title: "AI team-mate checks inventory to see if product is in stock",
            icon: "ri-warning-line text-[#FFD871] bg-[#0f172a]",
        },
        {
            title: "AI team-mate creates order",
            icon: "ri-user-line text-white  bg-[#465569]",
        },
        {
            title: "AI team-mate drafts order acknowledgement email",
            icon: "ri-user-line text-white  bg-[#465569]",
        },
        {
            title: "AI team-mate sends order acknowledgment email",
            icon: "ri-user-line text-white  bg-[#465569]",
        },
    ];

    const dispatch = useAppDispatch();
    const profile = getFromStorage("ally-user");
    const [testTaskId, setTestTaskId] = useState<string>("");
    const { testingAiTeammate, testAiTeammateSuccess } = useAppSelector((state) => state.workforce);
    const [testTask, setTestTask] = useState<{ [key: string]: any }>({});
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [showTestOutput, setShowTestOutput] = useState(false);
    const [testEmailModal, setTestEmailModal] = useState(false);
    const [confirmRetry, setConfirmRetry] = useState<boolean>(false);
    const [feedbackModal, setFeedbackModal] = useState<boolean>(false);
    const [feedbackSuccessModal, setFeedbackSuccessModal] = useState<boolean>(false);
    const [feedback, setFeedback] = useState<string>("");
    const [selectedStep, setSelectedStep] = useState<{ [key: string]: any }>({});
    const [testEmail, setTestEmail] = useState({
        subject: "",
        body: "",
    });

    const {
        approvingTaskStep,
        approveTaskStepSuccess,
        retryingTaskStep,
        retryTaskStepSuccess,
        givingTaskStepFeedback,
        giveTaskStepFeedbackSuccess,
        createSpsCommerceConnectionSuccess,
        creatingSpsCommerceConnection,
    } = useAppSelector((state) => state.workforce);

    const handleTest = () => {
        const body = {
            agentType: rawAgentType,
            message: testEmail?.body,
            subject: testEmail?.subject,
        };

        dispatch(workforceActions.testAiAgent(body));
    };

    const { data: taskInfo, isFetching: fetchingSingleTask } = useGetSingleTask(testTaskId);

    useEffect(() => {
        if (Boolean(testAiTeammateSuccess)) {
            setTestTaskId(testAiTeammateSuccess?._id);

            setTestEmailModal(false);
            setShowTestOutput(true);

            setTestEmail({
                body: "",
                subject: "",
            });
        }
        dispatch(workforceActions.resetTestAiAgentSuccess());
    }, [
        dispatch,
        testAiTeammateSuccess,
        // role,
        // avatar,
        // aiNameValue,
        // aiStepValues,
        // communicationChannels?.[0],
        // emailClientSocialId,
        // showTestOutput,
        // aiTeammateId,
    ]);

    useEffect(() => {
        if (taskInfo?._id) {
            setTestTask(taskInfo);
        }
        // removeFromStorage("connectedRedirect");
    }, [taskInfo]);
    // console.log(steps, "steps", testTask, "testTask");

    const {} = useChannel(
        `agent_tasks_${profile?.user?.parentId || profile?.user?._id}`,
        (taskData) => {
            console.log(taskData, "taskData");
            // if (taskData?.data?.task?._id === (testTaskId || taskId)) {
            if (taskData?.data?.task?._id === testTaskId) {
                const singleTaskCopy = { ...testTask };
                let singleTaskStepRanCopy = [...singleTaskCopy?.stepsRan];
                const decompressed = pako.inflate(taskData?.data?.step?.data, { to: "string" });
                const taskStep = JSON.parse(decompressed);
                const updatedStepIndex = singleTaskStepRanCopy?.findIndex(
                    (stepRan) => stepRan?.key === taskStep?.key
                );

                singleTaskCopy.state = taskData?.data?.task?.state;

                if (updatedStepIndex !== -1) {
                    singleTaskStepRanCopy[updatedStepIndex] = taskStep;
                } else {
                    singleTaskStepRanCopy = [...singleTaskStepRanCopy, taskStep];
                }
                singleTaskCopy.stepsRan = singleTaskStepRanCopy;

                setTestTask({
                    ...singleTaskCopy,
                    stepsRan: uniqBy(singleTaskStepRanCopy, "key"),
                });
                if (taskStep?.key === "order_email_send" || updatedStepIndex === 5) {
                    setIsProcessing(false);
                }
            }
        }
    );

    useEffect(() => {
        if (Boolean(retryTaskStepSuccess)) {
            setConfirmRetry(false);
            setIsProcessing(true);
            toast.custom((t) => (
                <CustomToast t={t} message={"Re-run successful"} type={"success"} />
            ));
            dispatch(workforceActions.resetRetryTaskStepSuccess());
        }
    }, [dispatch, retryTaskStepSuccess]);

    useEffect(() => {
        if (Boolean(giveTaskStepFeedbackSuccess)) {
            setFeedback("");
            setFeedbackModal(false);
            setFeedbackSuccessModal(true);

            dispatch(workforceActions.resetGiveeTaskStepFeedbackSuccess());
        }
    }, [dispatch, giveTaskStepFeedbackSuccess]);

    const handleRetry = () => {
        const body = {
            stepId: selectedStep?._id,
        };

        dispatch(workforceActions.retryTaskStep(body));
    };

    const handleSubmitFeedback = () => {
        const body = {
            stepId: selectedStep?._id,
            feedback: feedback,
        };

        dispatch(workforceActions.giveTaskStepFeedback(body));
    };

    const taskTableHeader = [
        { title: "Products", widthClass: "w-[41%]" },
        { title: "Current inventory", widthClass: "w-[31.7%]" },
        { title: "Status", widthClass: "w-[27%]" },
    ];

    const taskTableBody = (
        testTask?.state?.inventory_check_result?.length > 0
            ? testTask?.state?.inventory_check_result
            : testTask?.state?.order_info?.products
    )?.map((task, idx) => [
        {
            content: (
                <div
                    className={`py-3 pr-3 flex font-rocGroteskMedium items-center h-full w-full border-slate-100 text-sm  `}
                >
                    <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded bg-slate-200 flex items-center justify-center shrink-0">
                            <i className="ri-box-2-fill text-[17px] text-slate-500"></i>
                        </div>

                        <div className=" w-full">
                            <p className="text-sm">
                                {task?.inventory?.productName ||
                                    testTask?.state?.order_info?.products?.[idx]?.name ||
                                    "N/A"}
                            </p>

                            <p className="text-sm text-slate-500">{task?.inventory?.sku}</p>
                        </div>
                    </div>
                </div>
            ),
            // cellClickAction: () => navigate(`/dashboard/task/1`),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 flex font-rocGroteskMedium items-center h-full w-full border-slate-100 text-sm  `}
                >
                    <span>
                        {task?.currentStockLevel ??
                            task?.inventory?.productStockDetails?.stockLevel}
                    </span>
                </div>
            ),
        },
        {
            content: (
                <div className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm `}>
                    <div className="flex items-center space-x-2">
                        <span
                            className={`w-2 h-2 rounded-full ${
                                task?.currentStockLevel >= task?.currentOrderQty ||
                                task?.stockAvailable
                                    ? "bg-[#22C55E]"
                                    : "bg-[#EAB308]"
                            }`}
                        ></span>

                        <span>
                            {task?.currentStockLevel >= task?.currentOrderQty ||
                            task?.stockAvailable
                                ? "in stock"
                                : "out of stock"}
                        </span>
                    </div>
                </div>
            ),
        },
    ]);

    return (
        <>
            <div>
                <div className="sticky top-[-20px] z-[1200] bg-white ">
                    <div className="rounded-t-[8px]">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 text-sm ">
                            <div className="flex items-center space-x-1.5 text-slate-400 text-sm font-rocGroteskMedium">
                                <i
                                    onClick={() => {}}
                                    className="ri-arrow-left-s-line text-lg mt-[-2px] text-slate-400 py-0 px-1 rounded-full bg-slate-100 cursor-pointer"
                                ></i>
                                <span className="">AI teammate</span>
                                {agentType && <span className="">/ {agentType}</span>}

                                {agentName && (
                                    <span className="text-slate-900 font-rocGroteskMedium">
                                        / {agentName}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center space-x-3">
                                <Button
                                    btnText="Back to setup"
                                    btnClassname="!px-4 !py-2.5 !bg-tradeally-neutral-20 !w-fit"
                                    disabled={false}
                                    onClick={() => {
                                        setOpenPreview(false);
                                    }}
                                />
                                <Button
                                    btnText={"Activate"}
                                    btnPermission={[]}
                                    type="submit"
                                    isLoading={false}
                                    disabled={false}
                                    btnClassname="!px-4 !py-2.5 !w-fit"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="font-rocGroteskMedium bg-[url('https://storage.googleapis.com/heysynth_files/synth_webapp/Background%20pattern%20-%20dots.svg')] bg-repeat bg-cover ">
                    <div className="bg-white w-full p-4 border-slate-100">
                        <p className="text-gm-50 text-base">Previewing mode</p>
                    </div>

                    <div className="flex items-center flex-col  pb-20">
                        <div className="w-[90%] md:w-[610px] mx-auto px-4 mt-6 bg-white border border-slate-200 rounded-lg shadow-[0px_4px_18px_0px_rgba(0,0,0,0.04)]">
                            <p className="text-gm-50 text-base py-3 border-b border-slate-200">
                                Preview team-mate
                            </p>
                            <div className="p-4 bg-white border border-slate-200 rounded-lg mt-3 gap-2 flex justify-between items-center">
                                <div className="flex items-center gap-4">
                                    <img
                                        src="https://storage.googleapis.com/heysynth_files/synth_webapp/AI_Teammate_1_ure13x.svg"
                                        alt=""
                                        className="w-10 h-10"
                                    />
                                    <div className=" ">
                                        <p className="text-gm-50 mb-1 text-xs">AI teammate</p>
                                        <p className="text-g-75 text-base">
                                            {agentName
                                                ? `${agentName} - ${agentType}`
                                                : `${agentType}`}
                                        </p>
                                    </div>
                                </div>
                                {agentIsActive && (
                                    <div className="flex items-center gap-2  bg-[#D0F4EDB2] px-2.5 py-1.5 rounded-md">
                                        <p className="w-2 h-2 bg-[#22C55E] rounded-full flex items-center justify-center"></p>
                                        <p className="text-sm text-slate-900 ">Active</p>
                                    </div>
                                )}
                            </div>

                            {agentIsActive && (
                                <div
                                    className={`my-5 grid ${
                                        rawAgentType !== "orderProcessing"
                                            ? "grid-cols-2"
                                            : "grid-cols-1"
                                    } gap-2`}
                                >
                                    <div className="p-4 bg-white border border-slate-200 rounded-lg mt-2 gap-2 flex justify-between items-center">
                                        <div className=" ">
                                            <p className="text-gm-50 mb-1 text-xs">Trigger</p>
                                            <p className="text-g-75 text-base">
                                                {getTriggerName(trigger) || "Email"}
                                            </p>
                                        </div>
                                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#e1e7ef]">
                                            <i className="ri-flashlight-line text-base  text-slate-950"></i>
                                        </div>
                                    </div>
                                    {rawAgentType !== "orderProcessing" && (
                                        <div className="p-4 bg-white border border-slate-200 rounded-lg mt-2 gap-2 flex justify-between items-center">
                                            <div className=" ">
                                                <p className="text-gm-50 mb-1 text-xs">
                                                    Communication channels
                                                </p>
                                                <p className="text-g-75 text-base">
                                                    Email, Synth, Teams
                                                </p>
                                            </div>
                                            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#e1e7ef]">
                                                <i className="ri-chat-new-line text-base  text-slate-950"></i>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {agentIsActive ? (
                                <>
                                    <div className="flex items-center gap-2">
                                        <p className="bg-slate-200 flex-1 h-[1px] w-full"></p>
                                        <p className="text-slate-500 text-sm">
                                            AI team-mate workflow
                                        </p>
                                        <p className="bg-slate-200 flex-1 h-[1px] w-full"></p>
                                    </div>
                                    {!showTestOutput ? (
                                        <div className="pt-4 py-6">
                                            {steps?.map((step, idx) => {
                                                return (
                                                    <div key={idx}>
                                                        <div
                                                            className={` flex flex-col justify-center items-center font-rocGroteskMedium`}
                                                        >
                                                            <div
                                                                className={` flex items-center gap-2 border-slate-200 border rounded-lg rounded-b-lg p-3 bg-white `}
                                                            >
                                                                <i className="ri-flashlight-line text-xs py-[2px]  px-[4px] rounded-full bg-[#fdf3c7] text-[#D97706]"></i>
                                                                <p className="text-sm text-slate-800">
                                                                    {step?.description}
                                                                </p>
                                                                {step?.icon && (
                                                                    <i
                                                                        className={`${step?.icon} text-xs py-[2px]  px-[4px] rounded-full`}
                                                                    ></i>
                                                                )}
                                                            </div>

                                                            {aiFlow?.length - 1 !== idx && (
                                                                <img
                                                                    src="https://res.cloudinary.com/dvxi7qcmd/image/upload/v1737392368/arrow_down_cmoqw7.svg"
                                                                    alt="arrow down"
                                                                    height={36}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="space-y-[14px]">
                                            {testTask?.steps?.map((step, idx) => {
                                                return (
                                                    <div key={step?._id}>
                                                        <div className={` `}>
                                                            {idx === 0 && (
                                                                <div className="flex items-center space-x-2">
                                                                    <div className="flex items-center justify-center w-[21px] h-[21px] rounded-full bg-slate-200">
                                                                        <i className="ri-flashlight-line text-[13px]"></i>
                                                                    </div>
                                                                    <span className="text-xs text-gm-35">
                                                                        {testTask?.stepsRan?.[idx]
                                                                            ?.data?.answer
                                                                            ? "Email order is received and detected"
                                                                            : "Email received and detected not too be an order"}
                                                                    </span>
                                                                </div>
                                                            )}

                                                            {idx > 0 && (
                                                                <details open>
                                                                    <summary className="flex justify-between  cursor-pointer">
                                                                        <div className="flex items-center space-x-2">
                                                                            <div className="flex items-center justify-center w-[21px] h-[21px] rounded-full bg-slate-200">
                                                                                <i className="ri-flashlight-line text-[13px]"></i>
                                                                            </div>
                                                                            <span className="text-xs text-gm-35">
                                                                                {
                                                                                    testTask
                                                                                        ?.stepsRan?.[
                                                                                        idx
                                                                                    ]?.title
                                                                                }
                                                                            </span>
                                                                        </div>

                                                                        <i className="ri-arrow-down-s-fill"></i>
                                                                    </summary>

                                                                    <div className="mt-2.5 border border-slate-200 rounded-[6px] shadow-boxShadow-12">
                                                                        {testTask?.stepsRan?.[
                                                                            idx
                                                                        ] ? (
                                                                            <div className="p-4 border-b border-slate-200">
                                                                                <div>
                                                                                    <div className="flex items-center justify-between mb-2">
                                                                                        <span className="block text-base ">
                                                                                            {
                                                                                                testTask
                                                                                                    ?.stepsRan?.[
                                                                                                    idx
                                                                                                ]
                                                                                                    ?.title
                                                                                            }
                                                                                        </span>

                                                                                        <div className="flex items-center space-x-3">
                                                                                            {testTask?.stepsRan?.[
                                                                                                idx
                                                                                            ]?.status?.toLowerCase() ===
                                                                                            "completed" ? (
                                                                                                <div className="flex w-fit items-center space-x-2">
                                                                                                    <span className="w-2 h-2 rounded-full bg-[#22C55E]"></span>
                                                                                                    <span className="text-sm ">
                                                                                                        {
                                                                                                            testTask
                                                                                                                ?.stepsRan?.[
                                                                                                                idx
                                                                                                            ]
                                                                                                                ?.status
                                                                                                        }
                                                                                                    </span>
                                                                                                </div>
                                                                                            ) : !testTask?.stepsRan?.[
                                                                                                  idx
                                                                                              ]?.status
                                                                                                  ?.toLowerCase()
                                                                                                  ?.includes(
                                                                                                      "attention"
                                                                                                  ) ? (
                                                                                                <div className="flex w-fit items-center space-x-2">
                                                                                                    <span className="w-2 h-2 rounded-full bg-[#EAB308]"></span>
                                                                                                    <span className="text-sm ">
                                                                                                        {
                                                                                                            testTask
                                                                                                                ?.stepsRan?.[
                                                                                                                idx
                                                                                                            ]
                                                                                                                ?.status
                                                                                                        }
                                                                                                    </span>
                                                                                                </div>
                                                                                            ) : null}

                                                                                            {testTask?.stepsRan?.[
                                                                                                idx
                                                                                            ]?.status
                                                                                                ?.toLowerCase()
                                                                                                ?.includes(
                                                                                                    "attention"
                                                                                                ) && (
                                                                                                <div
                                                                                                    className={`w-fit pl-1.5 pr-2 py-1 bg-[#FEE2E2] flex items-center space-x-1.5 h-[25px] rounded`}
                                                                                                >
                                                                                                    <i
                                                                                                        className={`ri-information-line text-base text-[#DC2626]`}
                                                                                                    ></i>
                                                                                                    <span
                                                                                                        className={`text-xs text-[#DC2626]`}
                                                                                                    >
                                                                                                        User
                                                                                                        Attention
                                                                                                        Needed
                                                                                                    </span>
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>

                                                                                    {testTask
                                                                                        ?.stepsRan?.[
                                                                                        idx
                                                                                    ]?.approved ? (
                                                                                        <div className="space-y-1.5">
                                                                                            {testTask
                                                                                                ?.stepsRan?.[
                                                                                                idx
                                                                                            ]
                                                                                                ?.key ===
                                                                                                "order_check" && (
                                                                                                <p className="text-sm">
                                                                                                    <span className="">
                                                                                                        Is
                                                                                                        an
                                                                                                        order:
                                                                                                    </span>{" "}
                                                                                                    <span className="text-slate-500">
                                                                                                        {testTask
                                                                                                            ?.stepsRan?.[
                                                                                                            idx
                                                                                                        ]
                                                                                                            ?.data
                                                                                                            ?.answer
                                                                                                            ? "Yes"
                                                                                                            : "No"}
                                                                                                    </span>
                                                                                                </p>
                                                                                            )}

                                                                                            {(testTask
                                                                                                ?.stepsRan?.[
                                                                                                idx
                                                                                            ]
                                                                                                ?.key ===
                                                                                                "order_assistant" ||
                                                                                                testTask
                                                                                                    ?.stepsRan?.[
                                                                                                    idx
                                                                                                ]
                                                                                                    ?.key ===
                                                                                                    "order_create") && (
                                                                                                <div className="space-y-1">
                                                                                                    <p className="text-sm ">
                                                                                                        <span className="">
                                                                                                            Products:
                                                                                                        </span>{" "}
                                                                                                        <span className="text-slate-500">
                                                                                                            {testTask
                                                                                                                ?.state
                                                                                                                ?.order_info
                                                                                                                ?.products
                                                                                                                ?.length >
                                                                                                            0
                                                                                                                ? testTask?.state?.order_info?.products
                                                                                                                      ?.map(
                                                                                                                          (
                                                                                                                              product
                                                                                                                          ) =>
                                                                                                                              product?.name
                                                                                                                      )
                                                                                                                      ?.join(
                                                                                                                          ", "
                                                                                                                      )
                                                                                                                : "N/A"}
                                                                                                        </span>
                                                                                                    </p>
                                                                                                    <p className="text-sm ">
                                                                                                        <span className="">
                                                                                                            Customer:
                                                                                                        </span>{" "}
                                                                                                        <span className="text-slate-500">
                                                                                                            {testTask
                                                                                                                ?.state
                                                                                                                ?.order_info
                                                                                                                ?.company ||
                                                                                                                "N/A"}
                                                                                                        </span>
                                                                                                    </p>
                                                                                                    <p className="text-sm ">
                                                                                                        <span className="">
                                                                                                            Delivery
                                                                                                            date:
                                                                                                        </span>{" "}
                                                                                                        <span className="text-slate-500">
                                                                                                            {testTask
                                                                                                                ?.state
                                                                                                                ?.order_info
                                                                                                                ?.delivery_date
                                                                                                                ? moment(
                                                                                                                      testTask
                                                                                                                          ?.state
                                                                                                                          ?.order_info
                                                                                                                          ?.delivery_date,
                                                                                                                      "DD/MM/YYYY"
                                                                                                                  )?.format(
                                                                                                                      "MMM DD, YYYY"
                                                                                                                  )
                                                                                                                : "N/A"}
                                                                                                        </span>
                                                                                                    </p>
                                                                                                    <p className="text-sm ">
                                                                                                        <span className="">
                                                                                                            Delivery
                                                                                                            address:
                                                                                                        </span>{" "}
                                                                                                        <span className="text-slate-500">
                                                                                                            {testTask
                                                                                                                ?.state
                                                                                                                ?.order_info
                                                                                                                ?.delivery_address ||
                                                                                                                "N/A"}
                                                                                                        </span>
                                                                                                    </p>
                                                                                                </div>
                                                                                            )}

                                                                                            {testTask
                                                                                                ?.stepsRan?.[
                                                                                                idx
                                                                                            ]
                                                                                                ?.key ===
                                                                                                "inventory_check" && (
                                                                                                <CustomTable
                                                                                                    tableBody={
                                                                                                        taskTableBody
                                                                                                    }
                                                                                                    tableHeader={
                                                                                                        taskTableHeader
                                                                                                    }
                                                                                                    isScrollable={
                                                                                                        false
                                                                                                    }
                                                                                                    isCellBordered={
                                                                                                        true
                                                                                                    }
                                                                                                    isCellSelectable={
                                                                                                        false
                                                                                                    }
                                                                                                    isAllSelectable={
                                                                                                        false
                                                                                                    }
                                                                                                    headerContainerClass="!bg-slate-50"
                                                                                                    headerItemClass="font-rocGroteskMedium "
                                                                                                />
                                                                                            )}
                                                                                            {testTask
                                                                                                ?.stepsRan?.[
                                                                                                idx
                                                                                            ]
                                                                                                ?.key ===
                                                                                                "order_email_draft" && (
                                                                                                <div className="p-4 ">
                                                                                                    <span className=" text-sm font-rocGroteskMedium mb-[2px]">
                                                                                                        Subject:
                                                                                                    </span>{" "}
                                                                                                    <span className=" text-sm font-rocGroteskMedium mb-2">
                                                                                                        {
                                                                                                            testTask
                                                                                                                ?.stepsRan?.[
                                                                                                                idx
                                                                                                            ]
                                                                                                                ?.data
                                                                                                                ?.order_email_draft_subject
                                                                                                        }
                                                                                                    </span>
                                                                                                    <div
                                                                                                        className="text-sm text-slate-500"
                                                                                                        dangerouslySetInnerHTML={{
                                                                                                            __html: hasHTMLTag(
                                                                                                                testTask
                                                                                                                    ?.stepsRan?.[
                                                                                                                    idx
                                                                                                                ]
                                                                                                                    ?.data
                                                                                                                    ?.order_email_draft_content
                                                                                                            )
                                                                                                                ? sanitizeHtml(
                                                                                                                      testTask
                                                                                                                          ?.stepsRan?.[
                                                                                                                          idx
                                                                                                                      ]
                                                                                                                          ?.data
                                                                                                                          ?.order_email_draft_content
                                                                                                                  ).sanitizedHTML?.join(
                                                                                                                      ""
                                                                                                                  )
                                                                                                                : testTask
                                                                                                                      ?.stepsRan?.[
                                                                                                                      idx
                                                                                                                  ]
                                                                                                                      ?.data
                                                                                                                      ?.order_email_draft_content,
                                                                                                        }}
                                                                                                    />
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    ) : (
                                                                                        <p className="text-sm text-slate-500">
                                                                                            Please
                                                                                            approve
                                                                                            this
                                                                                            step to
                                                                                            proceed
                                                                                        </p>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="p-4 border-b border-slate-200">
                                                                                <div className="flex items-center justify-between mb-2">
                                                                                    <span
                                                                                        className={`text-xs px-1.5 flex items-center justify-center h-5 rounded border ${
                                                                                            testTask?.steps?.[
                                                                                                idx
                                                                                            ]?.status?.toLowerCase() ===
                                                                                            "completed"
                                                                                                ? "border-[#16A34A] text-[#16A34A] bg-[#DCFCE7]"
                                                                                                : "border-slate-200 bg-slate-100"
                                                                                        }`}
                                                                                    >
                                                                                        Step{" "}
                                                                                        {idx + 1}
                                                                                    </span>
                                                                                </div>

                                                                                <div>
                                                                                    <span className="block text-base mb-1">
                                                                                        {
                                                                                            testTask
                                                                                                ?.steps?.[
                                                                                                idx
                                                                                            ]?.title
                                                                                        }
                                                                                    </span>
                                                                                    <div className="space-y-1.5">
                                                                                        <p className="text-sm text-slate-500">
                                                                                            {
                                                                                                testTask
                                                                                                    ?.steps?.[
                                                                                                    idx
                                                                                                ]
                                                                                                    ?.description
                                                                                            }
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}

                                                                        {testTask?.stepsRan?.[
                                                                            idx
                                                                        ] && (
                                                                            <div className="p-4 flex items-center space-x-3">
                                                                                {!testTask
                                                                                    ?.stepsRan?.[
                                                                                    idx
                                                                                ]?.approved && (
                                                                                    <Button
                                                                                        btnText="Approve step"
                                                                                        btnPermission={[
                                                                                            AccountPermissions.all_access,
                                                                                            AccountPermissions.create_ai_teammate,
                                                                                        ]}
                                                                                        onClick={() => {
                                                                                            setSelectedStep(
                                                                                                testTask
                                                                                                    ?.stepsRan?.[
                                                                                                    idx
                                                                                                ]
                                                                                            );
                                                                                        }}
                                                                                        btnClassname="!px-[14px] !py-2 !w-fit !h-[36px]"
                                                                                    />
                                                                                )}

                                                                                {/* {testTask
                                                                                    ?.stepsRan?.[
                                                                                    idx
                                                                                ]?.key ===
                                                                                    "order_assistant" && (
                                                                                    <Button
                                                                                        btnText="Edit Order"
                                                                                        btnPermission={[
                                                                                            AccountPermissions.all_access,
                                                                                            AccountPermissions.create_ai_teammate,
                                                                                        ]}
                                                                                        icon={
                                                                                            <i className="ri-edit-2-line text-base"></i>
                                                                                        }
                                                                                        onClick={() => {
                                                                                            navigate(
                                                                                                `/dashboard/task/${testTaskId}/edit-order`,
                                                                                                {
                                                                                                    state: {
                                                                                                        internalNavigation:
                                                                                                            true,
                                                                                                    },
                                                                                                }
                                                                                            );
                                                                                        }}
                                                                                        btnClassname="!px-[14px] !py-2 !w-fit !h-[36px] !bg-transparent !border !border-slate-200"
                                                                                    />
                                                                                )} */}
                                                                                <Button
                                                                                    btnPermission={[
                                                                                        AccountPermissions.all_access,
                                                                                        AccountPermissions.create_ai_teammate,
                                                                                    ]}
                                                                                    btnText="Rerun"
                                                                                    icon={
                                                                                        <i className="ri-restart-line text-base"></i>
                                                                                    }
                                                                                    disabled={
                                                                                        !testTask
                                                                                            ?.stepsRan?.[
                                                                                            idx
                                                                                        ]?.approved
                                                                                    }
                                                                                    onClick={() => {
                                                                                        setSelectedStep(
                                                                                            testTask
                                                                                                ?.stepsRan?.[
                                                                                                idx
                                                                                            ]
                                                                                        );
                                                                                        setConfirmRetry(
                                                                                            true
                                                                                        );
                                                                                    }}
                                                                                    btnClassname="!px-[14px] !py-2 !w-fit !h-[36px] !bg-transparent !border !border-slate-200"
                                                                                />
                                                                                <Button
                                                                                    btnPermission={[
                                                                                        AccountPermissions.all_access,
                                                                                        AccountPermissions.create_ai_teammate,
                                                                                    ]}
                                                                                    btnText="Give feedback to this step"
                                                                                    icon={
                                                                                        <i className="ri-chat-2-line text-base"></i>
                                                                                    }
                                                                                    disabled={
                                                                                        !testTask
                                                                                            ?.stepsRan?.[
                                                                                            idx
                                                                                        ]?.approved
                                                                                    }
                                                                                    onClick={() => {
                                                                                        if (
                                                                                            testTask
                                                                                                ?.stepsRan?.[
                                                                                                idx
                                                                                            ]
                                                                                                ?.approved
                                                                                        ) {
                                                                                            setSelectedStep(
                                                                                                testTask
                                                                                                    ?.stepsRan?.[
                                                                                                    idx
                                                                                                ]
                                                                                            );
                                                                                            setFeedbackModal(
                                                                                                true
                                                                                            );
                                                                                        }
                                                                                    }}
                                                                                    btnClassname="!px-[14px] !py-2 !w-fit !h-[36px] !bg-transparent !border !border-slate-200"
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </details>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    <div className="flex  my-5 justify-center">
                                        <div
                                            onClick={() => {
                                                if (showTestOutput) {
                                                    setOpenPreview(false);
                                                } else {
                                                    steps?.length > 0 &&
                                                        agentType &&
                                                        setTestEmailModal(true);
                                                }
                                            }}
                                            className="h-[52px] bg-white w-fit rounded-[30px] flex items-center p-3 space-x-2 cursor-pointer shadow-[0px_4px_18px_0px_rgba(0,0,0,0.07)]"
                                        >
                                            <i className="ri-sparkling-2-line text-base bg-clip-text text-transparent bg-gradient-to-tr from-[#5C58FF] from-0% to-[#FF3D1D] to-100%"></i>

                                            {showTestOutput ? (
                                                <p className="text-sm text-gm-75">
                                                    Edit AI teammate
                                                </p>
                                            ) : (
                                                <p className="text-sm text-gm-75">
                                                    Test AI team-mate
                                                </p>
                                            )}

                                            <i className="ri-arrow-right-up-line"></i>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="h-[300px] "></div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <ModalContainer
                open={testEmailModal}
                showCloseIcon={false}
                tailwindClassName="w-[45%] max-lg:w-1/2 max-sm:w-[95%]"
                closeModal={() => {
                    setTestEmailModal(false);
                }}
            >
                <div className="bg-white rounded-lg shadow-cardShadow relative font-rocGroteskMedium">
                    <div className="flex items-center justify-between bg-[#424242] px-4 py-3">
                        <span className="text-sm text-white">New message</span>
                        <i
                            className="ri-close-fill text-white cursor-pointer"
                            onClick={() => {
                                setTestEmailModal(false);
                            }}
                        ></i>
                    </div>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleTest();
                        }}
                        className="bg-white min-h-[480px] px-4"
                    >
                        <div className="border-b border-slate-200">
                            <TextInput
                                value={testEmail?.subject || ""}
                                name={"subject"}
                                type={"text"}
                                required={true}
                                onChange={(e) => {
                                    setTestEmail((prev) => ({
                                        ...prev,
                                        subject: e.target.value,
                                    }));
                                }}
                                inputPlaceholder={"Subject"}
                                containerClassname=""
                                inputContainerClassname={"!border-0 !rounded-none "}
                                inputClassName="w-full "
                                inputContainerStyles={{ border: 0 }}
                            />
                        </div>

                        <div>
                            <TextareaInput
                                name={"body"}
                                value={testEmail?.body}
                                onChange={(e) => {
                                    setTestEmail((prev) => ({
                                        ...prev,
                                        body: e.target.value,
                                    }));
                                }}
                                inputContainerClassname={"!border-0 !rounded-none"}
                                inputClassName="!py-1.5 placeholder:!font-rocGroteskRegular"
                                rows={17}
                                inputPlaceholder={"Body"}
                                required={true}
                                inputContainerStyles={{ border: 0 }}
                            />
                        </div>

                        <div className="py-3 flex items-center justify-end border-t border-slate-200">
                            {/* <i className="ri-attachment-2 text-xl text-slate-500 cursor-pointer"></i> */}

                            <Button
                                btnText={"Send"}
                                type={"submit"}
                                btnPermission={[
                                    AccountPermissions.all_access,
                                    AccountPermissions.create_ai_teammate,
                                ]}
                                btnClassname="!py-2 !px-3 !w-fit"
                                // onClick={() => {
                                //     setTestEmailModal(false);
                                //     setShowTestOutput(true);
                                // }}
                                icon={<i className="ri-send-plane-fill"></i>}
                                isLoading={testingAiTeammate}
                            />
                        </div>
                    </form>
                </div>
            </ModalContainer>

            <ModalContainer
                open={confirmRetry}
                showCloseIcon={false}
                modalClassName={"py-5 px-5"}
                closeModal={() => setConfirmRetry(false)}
                tailwindClassName=" w-[42%] "
            >
                <div className="bg-white flex flex-col rounded-[8px]  ">
                    <div className="mt-5 px-6 flex justify-end ">
                        <i
                            onClick={() => setConfirmRetry(false)}
                            className="ri-close-fill text-gm-45 text-lg cursor-pointer "
                        ></i>
                    </div>
                    <div className="px-5 my-2 ">
                        <div>
                            <h6 className="text-center font-rocGroteskMedium text-[16px]">
                                You're about to rerun the agent task from this step. This will reset
                                progress from this point forward. Are you sure you want to proceed?
                            </h6>
                        </div>
                        <div className="mt-5 mb-3 space-x-2 flex items-center">
                            <Button
                                btnText={"No, close"}
                                type={"button"}
                                btnClassname="!py-3 !bg-n-20  !text-dark-gray-2 !px-4"
                                onClick={() => setConfirmRetry(false)}
                                disabled={retryingTaskStep}
                            />
                            <Button
                                btnText={"Yes, re-run step"}
                                type={"button"}
                                btnPermission={[
                                    AccountPermissions.all_access,
                                    AccountPermissions.create_ai_teammate,
                                ]}
                                btnClassname="!py-3 !bg-[#DCFCE7] border border-[#16A34A] !text-dark-gray-2 !px-4"
                                onClick={handleRetry}
                                isLoading={retryingTaskStep}
                            />
                        </div>
                    </div>
                </div>
            </ModalContainer>

            <ModalContainer
                open={feedbackModal}
                showCloseIcon={false}
                modalClassName={"py-5 px-5"}
                closeModal={() => setFeedbackModal(false)}
                tailwindClassName=" w-[42%] "
            >
                <div className="bg-white flex flex-col rounded-[8px]  ">
                    <div className="mt-5 px-6 flex justify-end ">
                        <i
                            onClick={() => setFeedbackModal(false)}
                            className="ri-close-fill text-gm-45 text-lg cursor-pointer "
                        ></i>
                    </div>
                    <div className="px-5 my-2 ">
                        <div className="mb-6">
                            <div className="flex justify-center">
                                <i className="ri-feedback-line text-[48px] mb-2.5"></i>
                            </div>

                            <h6 className="text-center font-rocGroteskMedium text-[22px]">
                                Provide Feedback on Completed Task
                            </h6>

                            <p className="text-base text-center font-rocGroteskMedium  text-slate-500 px-6">
                                Your AI teammate has completed the assigned task. Share your
                                feedback to help improve its future performance.
                            </p>
                        </div>

                        <div>
                            <div className="">
                                <TextareaInput
                                    name={"feedback"}
                                    value={feedback}
                                    onChange={(e) => setFeedback(e.target.value)}
                                    rows={5}
                                    placeholder={
                                        "Add or remove instructions, improvements, or suggestions here..."
                                    }
                                    containerClassname=""
                                />
                            </div>
                            <div>
                                <span className="text-xs text-slate-500 font-rocGroteskMedium">
                                    Once submitted, your feedback will be applied, and the task will
                                    re-run automatically
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="py-4 border-t border-slate-200 space-x-2 px-8 flex items-center">
                        <Button
                            btnText={"Cancel"}
                            type={"button"}
                            btnClassname="!py-3 !bg-n-20  !text-dark-gray-2 !px-4"
                            onClick={() => setFeedbackModal(false)}
                            disabled={givingTaskStepFeedback}
                        />
                        <Button
                            btnText={"Submit feedback and re-run step"}
                            type={"button"}
                            btnPermission={[
                                AccountPermissions.all_access,
                                AccountPermissions.create_ai_teammate,
                            ]}
                            btnClassname="!py-3 border !text-dark-gray-2 !px-4"
                            onClick={handleSubmitFeedback}
                            disabled={!feedback}
                            isLoading={givingTaskStepFeedback}
                        />
                    </div>
                </div>
            </ModalContainer>

            <ModalContainer
                open={feedbackSuccessModal}
                showCloseIcon={false}
                modalClassName={"py-5 px-5"}
                closeModal={() => setFeedbackSuccessModal(false)}
                tailwindClassName=" w-[42%] "
            >
                <div className="bg-white flex flex-col rounded-[8px]  ">
                    <div className="mt-5 px-6 flex justify-end ">
                        <i
                            onClick={() => setFeedbackSuccessModal(false)}
                            className="ri-close-fill text-gm-45 text-lg cursor-pointer "
                        ></i>
                    </div>
                    <div className="px-5 my-2 ">
                        <div className="mb-6">
                            <div className="flex justify-center">
                                <i className="ri-feedback-line text-[48px] mb-2.5"></i>
                            </div>

                            <h6 className="text-center font-rocGroteskMedium text-[22px]">
                                Feedback Received! Step Will Run Again
                            </h6>

                            <p className="text-base text-center font-rocGroteskMedium  text-slate-500 px-6">
                                Thank you for your feedback. Based on your input, this step will
                                automatically rerun to reflect the changes you suggested.
                            </p>
                        </div>

                        <div className="mb-8">
                            <span className="text-slate-500 font-rocGroteskMedium text-sm block mb-4">
                                What happens next:
                            </span>
                            <div className="border font-rocGroteskMedium border-slate-200 mb-4 rounded-[6px] px-2 py-3 shadow-[0px_4px_8px_-4px_#10182808]">
                                <ul className="list-disc text-sm ml-4 space-y-2">
                                    <li className="">
                                        Your AI teammate will update the task with the requested
                                        adjustments.
                                    </li>
                                    <li>
                                        The revised process will run again to ensure the task meets
                                        your expectations.
                                    </li>
                                </ul>
                            </div>

                            <span className="text-sm text-slate-500 font-rocGroteskMedium">
                                You'll be notified once the task is completed with the new changes.
                            </span>
                        </div>
                    </div>
                    <div className="py-4 border-t border-slate-200 space-x-2 px-8 flex items-center">
                        <Button
                            btnText={"Close"}
                            type={"button"}
                            btnClassname="!py-3 border !text-dark-gray-2 !px-4"
                            onClick={() => setFeedbackSuccessModal(false)}
                        />
                    </div>
                </div>
            </ModalContainer>
        </>
    );
};

export default Preview;
